
import {
  Vue, Component,
} from 'vue-property-decorator';
import { Route } from 'vue-router';
import API from '@/lib/API';
import {
  RuleEvent, RuleConditionDefinition, RuleActionDefinition,
  RuleCondition, RuleAction, RuleFormData, Rule,
  Category, Tag, User, RuleConditionItem,
  RuleArchiveEntry, RuleCustomValue,
} from '@/types';
import {
  hydrateConditionItems, isBlock, hydrateAction, isCustomValueComplete, applyDefaultSender,
} from '@/helpers/ruleHelpers';
import { currentUserHasPermission } from '@/helpers';
import RuleConditionBuilder from '@/components/rules/RuleConditionBuilder.vue';
import RuleActionBuilder from '@/components/rules/RuleActionBuilder.vue';
import RuleDeleteDialog from '@/components/rules/RuleDeleteDialog.vue';
import RuleArchivePayload from '@/components/rules/RuleArchivePayload.vue';

// Perche' il caricamento e' fallito, in una forma che il template sa mostrare:
// il messaggio, e dove si torna. La destinazione cambia col caso, chi veniva
// dall'archivio vuole tornare all'archivio, non all'elenco delle regole.
interface RuleLoadError {
  message: string;
  backTo: string;
  backLabel: string;
}

@Component({
  components: {
    RuleConditionBuilder,
    RuleActionBuilder,
    RuleDeleteDialog,
    RuleArchivePayload,
  },
})
export default class RuleEditor extends Vue {
  loading = true

  saving = false

  formValid = false

  showDeleteDialog = false

  leaveDialog = false

  pendingNext: Function | null = null

  initialRuleSnapshot = ''

  archiveEntry: RuleArchiveEntry | null = null

  missingRefs: string[] = []

  loadError: RuleLoadError | null = null

  get pageTitle(): string {
    if (this.loadError) return 'Regola non disponibile';
    if (this.isEditMode) return 'Modifica Regola';
    return this.archiveEntry ? 'Nuova Regola dall\'archivio' : 'Nuova Regola';
  }

  get archiveId(): number | null {
    const q = this.$route.query.archivio;
    return q ? parseInt(String(q), 10) : null;
  }

  rule: RuleFormData = {
    name: '',
    order: 99999,
    event: null,
    conditions: [],
    when: 'all',
    actions: [],
  }

  localUsers: User[] = []

  localGroups: any[] = []

  localCategories: Category[] = []

  localTags: Tag[] = []

  localTemplates: any[] = []

  localChecklists: any[] = []

  whenOptions = [
    { value: 'all', text: 'Esegui le azioni quando TUTTE le condizioni sono soddisfatte' },
    { value: 'one', text: 'Esegui le azioni quando ALMENO UNA condizione è soddisfatta' },
  ]

  get isEditMode(): boolean {
    return !!this.$route.params.id;
  }

  get events(): RuleEvent[] {
    return this.$store.getters.ruleEvents || [];
  }

  get conditionDefinitions(): RuleConditionDefinition[] {
    return this.$store.getters.ruleConditionDefinitions || [];
  }

  get actionDefinitions(): RuleActionDefinition[] {
    return this.$store.getters.ruleActionDefinitions || [];
  }

  get eventCodename(): string | null {
    return this.rule.event ? this.rule.event.codename : null;
  }

  onEventChange(codename: string | null): void {
    this.rule.event = codename
      ? this.events.find((e) => e.codename === codename) || null
      : null;
  }

  get isFormComplete(): boolean {
    const effectiveConditionCount = this.rule.conditions.reduce((count: number, item: RuleConditionItem) => {
      if (isBlock(item)) {
        return count + item.conditions.length;
      }
      return count + 1;
    }, 0);
    const hasConditions = effectiveConditionCount > 0;
    return !!this.rule.name
      && !!this.rule.name.trim()
      && this.rule.order !== null && this.rule.order !== undefined
      && !!this.rule.event
      && hasConditions
      && this.rule.actions.length > 0
      && !this.hasEmptyEntityValues;
  }

  // Solo in recupero dall'archivio: un valore entita' svuotato perche'
  // l'entita' non esiste piu' blocca il salvataggio (il backend legge
  // value.id e fallirebbe); blocca anche una lista multipla (utenti/gruppi)
  // svuotata del tutto, altrimenti la regola verrebbe creata con un
  // destinatario vuoto che non fa nulla in silenzio. Non blocca i valori
  // facoltativi (gli elenchi "Eccetto..."): vuoti escludono nessuno, come un
  // gruppo che nel nuovo portale non esiste.
  get hasEmptyEntityValues(): boolean {
    if (!this.archiveEntry) return false;
    const entityTypes = ['user', 'group', 'category', 'tag', 'template', 'checklist'];
    const check = (cvs: RuleCustomValue[]) => cvs.some(
      (cv) => entityTypes.includes(cv.type) && !isCustomValueComplete(cv),
    );
    const walk = (items: RuleConditionItem[]): boolean => items.some((item) => (isBlock(item)
      ? walk(item.conditions)
      : check((item as RuleCondition).customValues || [])));
    return walk(this.rule.conditions) || this.rule.actions.some((a) => check(a.customValues || []));
  }

  // Svuota i valori che puntano a entita' assenti nel nuovo portale e ne
  // annota il nome per l'avviso.
  pruneMissingReferences() {
    const lists: Record<string, any[]> = {
      user: this.localUsers,
      group: this.localGroups,
      category: this.localCategories,
      tag: this.localTags,
      template: this.localTemplates,
      checklist: this.localChecklists,
    };
    const labels: Record<string, string> = {
      user: 'utente', group: 'gruppo', category: 'categoria', tag: 'tag', template: 'modello', checklist: 'checklist',
    };
    // Trova l'oggetto della lista locale (nuovo portale) per id: usare
    // quello, non il payload dell'archivio, cosi' l'autocomplete mostra il
    // nome attuale e non quello vecchio con cui l'entita' e' stata salvata.
    const find = (type: string, id: number) => (lists[type] || []).find((x) => x.id === id);
    // Scarta silenziosamente i dati malformati (non oggetti, o senza id
    // numerico): niente riga "undefined" in missingRefs, niente crash.
    const isEntity = (v: any) => v !== null && v !== undefined && typeof v === 'object' && typeof v.id === 'number';
    const prune = (cvs: RuleCustomValue[]) => cvs.forEach((cv) => {
      if (!(cv.type in lists)) return;
      if (Array.isArray(cv.value)) {
        const keep: any[] = [];
        cv.value.forEach((v: any) => {
          if (!isEntity(v)) return;
          const trovato = find(cv.type, v.id);
          if (trovato) {
            keep.push(trovato);
          } else {
            this.missingRefs.push(`${labels[cv.type]}: ${v.name || v.id}`);
          }
        });
        cv.value = keep; // eslint-disable-line no-param-reassign
      } else if (cv.value !== null && cv.value !== undefined) {
        if (!isEntity(cv.value)) {
          cv.value = null; // eslint-disable-line no-param-reassign
          return;
        }
        const trovato = find(cv.type, cv.value.id);
        if (trovato) {
          cv.value = trovato; // eslint-disable-line no-param-reassign
        } else {
          this.missingRefs.push(`${labels[cv.type]}: ${cv.value.name || cv.value.id}`);
          cv.value = null; // eslint-disable-line no-param-reassign
        }
      }
    });
    const walk = (items: RuleConditionItem[]) => items.forEach((item) => (isBlock(item)
      ? walk(item.conditions)
      : prune((item as RuleCondition).customValues || [])));
    walk(this.rule.conditions);
    this.rule.actions.forEach((a) => prune(a.customValues || []));
    this.missingRefs = [...new Set(this.missingRefs)];
  }

  hasPermission(codename: string): boolean {
    return currentUserHasPermission(codename);
  }

  get isDirty(): boolean {
    return JSON.stringify(this.rule) !== this.initialRuleSnapshot;
  }

  // eslint-disable-next-line class-methods-use-this
  beforeRouteLeave(_to: Route, _from: Route, next: Function) {
    if (!this.isDirty) {
      next();
      return;
    }
    this.pendingNext = next;
    this.leaveDialog = true;
    next(false);
  }

  goBack() {
    if (this.isDirty) {
      this.pendingNext = () => this.$router.push('/r');
      this.leaveDialog = true;
    } else {
      this.$router.push('/r');
    }
  }

  leave() {
    this.leaveDialog = false;
    this.initialRuleSnapshot = JSON.stringify(this.rule);
    if (this.pendingNext) {
      this.pendingNext();
      this.pendingNext = null;
    } else {
      this.$router.push('/r');
    }
  }

  async save() {
    this.saving = true;

    try {
      const payload = this.buildPayload();

      let response: any;
      if (this.isEditMode) {
        response = await API.updateRule(parseInt(this.$route.params.id, 10), payload);
      } else {
        response = await API.createRule(payload);
      }

      if (response instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('RuleEditor: save error', response);
        return;
      }

      let ruleId = '';
      if (this.isEditMode) {
        ruleId = this.$route.params.id;
      } else if (response && response.id) {
        ruleId = response.id;
      }

      this.initialRuleSnapshot = JSON.stringify(this.rule);
      this.$router.push({ path: '/r', query: { hl: String(ruleId) } });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('RuleEditor: save error', error);
    } finally {
      this.saving = false;
    }
  }

  async duplicate() {
    this.saving = true;

    try {
      const payload = this.buildPayload();
      payload.name = `${payload.name} (COPIA)`;

      const response = await API.createRule(payload);

      if (response instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('RuleEditor: duplicate error', response);
        return;
      }

      const ruleId = response && response.id ? response.id : '';
      this.$router.push({ path: '/r', query: { hl: String(ruleId) } });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('RuleEditor: duplicate error', error);
    } finally {
      this.saving = false;
    }
  }

  async deleteRule() {
    try {
      const id = parseInt(this.$route.params.id, 10);
      const response = await API.deleteRule(id);

      if (response instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('RuleEditor: delete error', response);
        return;
      }

      this.$router.push('/r');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('RuleEditor: delete error', error);
    } finally {
      this.showDeleteDialog = false;
    }
  }

  buildPayload(): any {
    const serializeItem = (item: RuleConditionItem): any => {
      if (isBlock(item)) {
        return {
          block: item.block,
          conditions: item.conditions.map(serializeItem),
        };
      }
      const cond = item as RuleCondition;
      return {
        codename: cond.codename,
        customValues: cond.customValues,
      };
    };
    return {
      name: this.rule.name,
      order: this.rule.order,
      event: this.rule.event,
      when: this.rule.when,
      conditions: this.rule.conditions.map(serializeItem),
      actions: this.rule.actions.map((a, index) => ({
        codename: a.codename,
        name: a.name,
        order: a.order !== undefined ? a.order : index,
        customValues: a.customValues,
      })),
    };
  }

  // Il client API restituisce l'errore axios cosi' com'e', quindi lo stato HTTP
  // si legge da error.response.status. Distinguere il 404 dal resto non e'
  // pignoleria: "non esiste" detto per una rete caduta manda a cercare la
  // regola nel posto sbagliato, e qui il portale gira su piu' istanze.
  buildLoadError(err: unknown, soggetto: string, backTo: string, backLabel: string): RuleLoadError {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    const message = status === 404
      ? `${soggetto} non esiste.`
      : `Non è stato possibile caricare ${soggetto.toLowerCase()}. Riprova fra poco.`;

    return { message, backTo, backLabel };
  }

  async created() {
    try {
      // Load metadata (events, conditions, actions definitions)
      await this.$store.dispatch('fetchRuleMetadata');

      // Load reference data in parallel
      const [, groups, , , templates, checklists] = await Promise.all([
        this.$store.dispatch('initUsers'),
        API.getGroups(),
        this.$store.dispatch('initCategories'),
        this.$store.dispatch('initTags'),
        API.getTemplates(),
        API.getChecklists(),
      ]);

      // Populate local data from store and API responses
      this.localUsers = this.$store.getters.users || [];
      this.localGroups = Array.isArray(groups) ? groups : [];
      this.localCategories = this.$store.getters.categories || [];
      this.localTags = this.$store.getters.tags || [];
      this.localTemplates = Array.isArray(templates) ? templates : [];
      this.localChecklists = Array.isArray(checklists) ? checklists : [];

      // If editing, load existing rule
      if (this.isEditMode) {
        const ruleId = parseInt(this.$route.params.id, 10);
        const ruleData: Rule = await API.getRule(ruleId);

        if (ruleData instanceof Error) {
          // eslint-disable-next-line no-console
          console.error('RuleEditor: error loading rule', ruleData);
          this.loadError = this.buildLoadError(
            ruleData, `La regola #${ruleId}`, '/r', 'Torna alle regole',
          );
          this.loading = false;
          return;
        }

        this.rule.name = ruleData.name;
        this.rule.order = ruleData.order;
        this.rule.when = ruleData.when;

        // Match event to definition
        if (ruleData.event) {
          this.rule.event = this.events.find(
            (e) => e.codename === ruleData.event!.codename,
          ) || null;
        }

        // Hydrate conditions
        this.rule.conditions = hydrateConditionItems(ruleData.conditions || [], this.conditionDefinitions);

        // Hydrate actions
        this.rule.actions = (ruleData.actions || [])
          .map((a: RuleAction) => hydrateAction(a, this.actionDefinitions))
          .filter((a): a is RuleAction => a !== null);
      }

      if (!this.isEditMode && this.archiveId) {
        const entry: RuleArchiveEntry = await API.getRulesArchiveEntry(this.archiveId);
        if (!entry || entry instanceof Error || !entry.payload) {
          // eslint-disable-next-line no-console
          console.error('RuleEditor: voce d\'archivio non caricata', entry);
          // Voce presente ma senza payload: esiste e non si riesce a leggere,
          // che non e' la stessa cosa di "non esiste".
          this.loadError = (entry && !(entry instanceof Error) && !entry.payload)
            ? {
              message: `La voce #${this.archiveId} dell'archivio non è leggibile.`,
              backTo: '/r/archivio',
              backLabel: 'Torna all\'archivio',
            }
            : this.buildLoadError(
              entry, `La voce #${this.archiveId} dell'archivio`, '/r/archivio', 'Torna all\'archivio',
            );
        } else {
          this.archiveEntry = entry;
          const p = entry.payload;
          this.rule.name = p.name;
          this.rule.order = p.order;
          this.rule.when = p.when;
          this.rule.event = p.event
            ? this.events.find((e) => e.codename === p.event!.codename) || null
            : null;
          this.rule.conditions = hydrateConditionItems(p.conditions || [], this.conditionDefinitions);
          this.rule.actions = (p.actions || [])
            .map((a: RuleAction) => hydrateAction(a, this.actionDefinitions))
            .filter((a): a is RuleAction => a !== null);
          // L'import ha scritto nell'archivio lo stesso mittente su tutte le
          // azioni, con il nome di allora del portale: si recupera con quello di
          // oggi (OwlCaveLabs/syndesia-l2c-support#46)
          this.rule.actions.forEach((a) => applyDefaultSender(a.customValues, false));
          this.pruneMissingReferences();
        }
      }
      // Snapshot per dirty tracking
      this.initialRuleSnapshot = JSON.stringify(this.rule);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('RuleEditor: error in created()', error);
    } finally {
      this.loading = false;
    }
  }
}
